import React from 'react';
import { Building2, Copy, CheckCircle2 } from 'lucide-react';

export function OrganizationDetails() {
  const [copiedField, setCopiedField] = React.useState<null | 'name' | 'krs' | 'nip' | 'regon'>(null);

  const copyValue = async (value: string, field: 'name' | 'krs' | 'nip' | 'regon') => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = value;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    }
  };

  return (
    <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-8 rounded-lg shadow-lg">
      <div className="flex items-center gap-4 mb-6">
        <div className="w-12 h-12 rounded-full bg-accessible-red-600/10 flex items-center justify-center">
          <Building2 className="w-6 h-6 text-accessible-red-600" aria-hidden="true" />
        </div>
        <h2 className="card-title">
          Dane organizacji
        </h2>
      </div>
      
      <div className="space-y-6">
        {/* OPP Status */}
        <div>
          <h3 className="heading-4 mb-4">
            Organizacja Pożytku Publicznego
          </h3>
          <div className="space-y-4">
            <div className="bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black p-6 rounded-lg">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="label-large mb-1">
                    Status OPP 
                  </h4>
                  <p className="body-medium no-margin">
                    Tak
                  </p>
                </div>
                <div>
                  <h4 className="label-large mb-1">
                    Data przyznania statusu OPP
                  </h4>
                  <p className="body-medium no-margin">
                    20.01.2023
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Registration Details */}
        <div>
          <h3 className="heading-4 mb-4">
            Rejestr Stowarzyszeń
          </h3>
          <div className="bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black p-6 rounded-lg">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="label-large mb-1">
                  Data wpisu
                </h4>
                <p className="body-medium no-margin">
                  24.10.2012
                </p>
              </div>
              <div>
                <h4 className="label-large mb-1">
                  Data wykreślenia
                </h4>
                <p className="body-medium no-margin">
                  -
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Basic Information */}
        <div>
          <h3 className="heading-4 mb-4">
            Podstawowe informacje
          </h3>
          <div className="bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black p-6 rounded-lg">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="label-large mb-1">
                  Nazwa
                </h4>
                <div className="flex items-center justify-between bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-white hc-on-white rounded p-2">
                  <p className="body-medium no-margin text-accessible-gray-900 dark:text-white">
                    ZNAJDKI
                  </p>
                  <button
                    type="button"
                    onClick={() => copyValue('ZNAJDKI', 'name')}
                    className="p-2 hover:bg-accessible-gray-200 dark:hover:bg-accessible-gray-600 rounded-lg transition-colors"
                    title="Skopiuj nazwę"
                    aria-label="Skopiuj nazwę"
                  >
                    {copiedField === 'name' ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600" aria-hidden="true" />
                    ) : (
                      <Copy className="w-5 h-5 text-accessible-gray-700 dark:text-white/90 high-contrast:text-white" aria-hidden="true" />
                    )}
                  </button>
                </div>
              </div>
              <div>
                <h4 className="label-large mb-1">
                  Forma prawna
                </h4>
                <p className="body-medium no-margin">
                  FUNDACJA
                </p>
              </div>
              <div>
                <h4 className="label-large mb-1">
                  KRS
                </h4>
                <div className="flex items-center justify-between bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-white hc-on-white rounded p-2">
                  <p className="body-medium no-margin text-accessible-gray-900 dark:text-white">
                    0000437387
                  </p>
                  <button
                    type="button"
                    onClick={() => copyValue('0000437387', 'krs')}
                    className="p-2 hover:bg-accessible-gray-200 dark:hover:bg-accessible-gray-600 rounded-lg transition-colors"
                    title="Skopiuj numer KRS"
                    aria-label="Skopiuj numer KRS"
                  >
                    {copiedField === 'krs' ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600" aria-hidden="true" />
                    ) : (
                      <Copy className="w-5 h-5 text-accessible-gray-700 dark:text-white/90 high-contrast:text-white" aria-hidden="true" />
                    )}
                  </button>
                </div>
              </div>
              <div>
                <h4 className="label-large mb-1">
                  NIP
                </h4>
                <div className="flex items-center justify-between bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-white hc-on-white rounded p-2">
                  <p className="body-medium no-margin text-accessible-gray-900 dark:text-white">
                    1132860384
                  </p>
                  <button
                    type="button"
                    onClick={() => copyValue('1132860384', 'nip')}
                    className="p-2 hover:bg-accessible-gray-200 dark:hover:bg-accessible-gray-600 rounded-lg transition-colors"
                    title="Skopiuj NIP"
                    aria-label="Skopiuj NIP"
                  >
                    {copiedField === 'nip' ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600" aria-hidden="true" />
                    ) : (
                      <Copy className="w-5 h-5 text-accessible-gray-700 dark:text-white/90 high-contrast:text-white" aria-hidden="true" />
                    )}
                  </button>
                </div>
              </div>
              <div>
                <h4 className="label-large mb-1">
                  REGON
                </h4>
                <div className="flex items-center justify-between bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-white hc-on-white rounded p-2">
                  <p className="body-medium no-margin text-accessible-gray-900 dark:text-white">
                    14639078100000
                  </p>
                  <button
                    type="button"
                    onClick={() => copyValue('14639078100000', 'regon')}
                    className="p-2 hover:bg-accessible-gray-200 dark:hover:bg-accessible-gray-600 rounded-lg transition-colors"
                    title="Skopiuj REGON"
                    aria-label="Skopiuj REGON"
                  >
                    {copiedField === 'regon' ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600" aria-hidden="true" />
                    ) : (
                      <Copy className="w-5 h-5 text-accessible-gray-700 dark:text-white/90 high-contrast:text-white" aria-hidden="true" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Address */}
        <div>
          <h3 className="heading-4 mb-4">
            Siedziba i adres
          </h3>
          <div className="bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black p-6 rounded-lg">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="label-large mb-1">
                  Adres
                </h4>
                <p className="body-medium no-margin">
                  UL. LEONA BERENSONA 90
                </p>
              </div>
              <div>
                <h4 className="label-large mb-1">
                  Kod pocztowy
                </h4>
                <p className="body-medium no-margin">
                  03-287
                </p>
              </div>
              <div>
                <h4 className="label-large mb-1">
                  Miejscowość
                </h4>
                <p className="body-medium no-margin">
                  WARSZAWA
                </p>
              </div>
              <div>
                <h4 className="label-large mb-1">
                  Województwo
                </h4>
                <p className="body-medium no-margin">
                  MAZOWIECKIE
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}